import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  Pencil,
  Search,
  Trash2,
  Users as UsersIcon,
  Shield,
  FolderTree,
  Building2,
  FileSignature,
  Activity,
} from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { EntityPickerField, type PickedEntity } from "@/components/ui/EntityPicker";
import { RowActions } from "@/components/ui/RowActions";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { UserSummary } from "@pkhoffice/shared";

type Kind = "users" | "roles" | "groups" | "orgs" | "sign-flows" | "activity-log";

const TITLES: Record<Kind, { title: string; breadcrumb: string; icon: typeof UsersIcon }> = {
  users: { title: "Danh sách người dùng", breadcrumb: "Quản trị › Người dùng", icon: UsersIcon },
  roles: { title: "Danh sách vai trò", breadcrumb: "Quản trị › Vai trò", icon: Shield },
  orgs: { title: "Danh sách đơn vị", breadcrumb: "Quản trị › Đơn vị", icon: Building2 },
  groups: { title: "Danh sách nhóm cá nhân", breadcrumb: "Quản trị › Nhóm", icon: FolderTree },
  "sign-flows": {
    title: "Danh sách Luồng ký mẫu",
    breadcrumb: "Quản trị › Luồng ký mẫu",
    icon: FileSignature,
  },
  "activity-log": {
    title: "Lịch sử hoạt động",
    breadcrumb: "Quản trị › Lịch sử hoạt động",
    icon: Activity,
  },
};

const inputClass =
  "w-full h-9 pl-10 pr-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function SimpleSearchBar({
  q,
  setQ,
  onSubmit,
  placeholder,
}: {
  q: string;
  setQ: (s: string) => void;
  onSubmit: () => void;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="flex-1 max-w-md relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          className={inputClass}
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
        />
      </div>
      <Button variant="outline" size="sm" onClick={onSubmit}>
        Tìm kiếm
      </Button>
    </div>
  );
}

/* --- /admin/users --- */
type UserFilters = {
  fullName: string;
  employeeCode: string;
  email: string;
  phone: string;
  loginName: string;
  enabled: string;
  roleId: string;
  org: PickedEntity | null;
};
const EMPTY_USER_FILTERS: UserFilters = {
  fullName: "",
  employeeCode: "",
  email: "",
  phone: "",
  loginName: "",
  enabled: "",
  roleId: "",
  org: null,
};

function UsersPanel() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<UserFilters>(EMPTY_USER_FILTERS);
  const [applied, setApplied] = useState<UserFilters>(EMPTY_USER_FILTERS);

  const { data: roles } = useQuery<RoleRow[]>({
    queryKey: ["admin-roles"],
    queryFn: async () => {
      const r = await api.get("/role/list");
      return (r.data.data ?? r.data) as RoleRow[];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", page, pageSize, applied],
    queryFn: async () => {
      const { data } = await api.get("/user/list", {
        params: {
          page,
          pageSize,
          fullName: applied.fullName || undefined,
          employeeCode: applied.employeeCode || undefined,
          email: applied.email || undefined,
          phone: applied.phone || undefined,
          loginName: applied.loginName || undefined,
          enabled: applied.enabled || undefined,
          roleId: applied.roleId || undefined,
          orgId: applied.org?.id,
        },
      });
      return data.data ?? data;
    },
  });

  const setF = <K extends keyof UserFilters>(k: K, v: UserFilters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));
  const textField = (label: string, key: keyof UserFilters) => ({
    label,
    control: (
      <input
        className={filterInputClass}
        value={filters[key] as string}
        onChange={(e) => setF(key, e.target.value as never)}
      />
    ),
  });

  // Bộ cột theo đúng lưới hệ cũ: STT, Mã nhân viên, Tên đăng nhập, Họ tên,
  // "Phòng ban || Vai trò" và "Số điện thoại và email" đều gộp hai dòng một ô.
  const columns: Column<UserSummary>[] = [
    {
      key: "stt",
      header: "STT",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "employeeCode",
      header: "Mã nhân viên",
      width: "120px",
      render: (r) => <span className="font-mono text-xs">{r.employeeCode ?? "—"}</span>,
    },
    {
      key: "login",
      header: "Tên đăng nhập",
      width: "140px",
      render: (r) => <span className="font-mono text-xs">{r.loginName}</span>,
    },
    {
      key: "name",
      header: "Họ tên",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{r.fullName ?? "—"}</div>
          {!r.isEnabled && <StatusBadge variant="neutral">Khoá</StatusBadge>}
        </div>
      ),
    },
    {
      key: "orgRole",
      header: "Phòng ban || Vai trò",
      width: "260px",
      render: (r) => (
        <div className="min-w-0">
          <div className="text-sm truncate">{r.orgName ?? "—"}</div>
          <div className="text-xs text-muted-foreground truncate">{r.roleName ?? ""}</div>
        </div>
      ),
    },
    {
      key: "contact",
      header: "Số điện thoại và email",
      width: "240px",
      render: (r) => (
        <div className="min-w-0">
          <div className="text-sm">{r.mobile ?? "—"}</div>
          <div className="text-xs text-muted-foreground truncate">{r.email ?? ""}</div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={TITLES.users.title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} người dùng`}
        breadcrumb={TITLES.users.breadcrumb}
      />
      <SearchPanel
        fields={[
          textField("Họ tên", "fullName"),
          textField("Mã nhân viên", "employeeCode"),
          textField("Email", "email"),
          textField("Số điện thoại", "phone"),
          textField("Tên đăng nhập", "loginName"),
          {
            label: "Trạng thái",
            control: (
              <select
                className={filterInputClass}
                value={filters.enabled}
                onChange={(e) => setF("enabled", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                <option value="true">Hoạt động</option>
                <option value="false">Khoá</option>
              </select>
            ),
          },
          {
            label: "Vai trò",
            control: (
              <select
                className={filterInputClass}
                value={filters.roleId}
                onChange={(e) => setF("roleId", e.target.value)}
              >
                <option value="">---- Tất cả ----</option>
                {(roles ?? []).map((r) => (
                  <option key={r.roleId} value={r.roleId}>
                    {r.name ?? r.code ?? r.roleId}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: "Đơn vị",
            control: (
              <EntityPickerField kind="org" value={filters.org} onChange={(v) => setF("org", v)} />
            ),
          },
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
      />
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.staffId}
        isLoading={isLoading}
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}

/* --- /admin/roles --- */
type RoleRow = {
  roleId: number;
  code: string | null;
  name: string | null;
  description: string | null;
};
function RolesPanel() {
  const { data, isLoading } = useQuery<RoleRow[]>({
    queryKey: ["admin-roles"],
    queryFn: async () => {
      const r = await api.get("/role/list");
      return (r.data.data ?? r.data) as RoleRow[];
    },
  });
  const rows = data ?? [];
  const columns: Column<RoleRow>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => <span className="text-muted-foreground tabular-nums">{i + 1}</span>,
    },
    {
      key: "code",
      header: "Mã vai trò",
      width: "140px",
      render: (r) => <span className="font-mono text-xs">{r.code ?? "—"}</span>,
    },
    {
      key: "name",
      header: "Tên vai trò",
      render: (r) => <span className="font-medium">{r.name ?? "—"}</span>,
    },
    {
      key: "desc",
      header: "Mô tả",
      render: (r) => <span className="text-sm text-muted-foreground">{r.description ?? "—"}</span>,
    },
    {
      key: "id",
      header: "ID",
      width: "90px",
      render: (r) => <span className="font-mono text-xs tabular-nums">{r.roleId}</span>,
    },
  ];
  return (
    <div>
      <PageHeader
        title={TITLES.roles.title}
        subtitle={`${rows.length.toLocaleString("vi-VN")} vai trò`}
        breadcrumb={TITLES.roles.breadcrumb}
      />
      <DataTable
        columns={columns}
        rows={rows}
        keyBy={(r) => r.roleId}
        isLoading={isLoading}
        dense
      />
    </div>
  );
}

/* --- /admin/orgs --- */
type OrgListRow = {
  orgId: number;
  code: string | null;
  name: string | null;
  parentId: number | null;
  parentName: string | null;
  path: string | null;
  level: number | null;
  isActive: boolean;
};
function OrgsPanel() {
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orgs", page, pageSize, submittedQ],
    queryFn: async () => {
      const { data } = await api.get("/org/list", {
        params: { page, pageSize: pageSize, q: submittedQ || undefined },
      });
      return data.data ?? data;
    },
  });

  const columns: Column<OrgListRow>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "code",
      header: "Mã",
      width: "160px",
      render: (r) => <span className="font-mono text-xs">{r.code ?? "—"}</span>,
    },
    {
      key: "name",
      header: "Tên đơn vị",
      render: (r) => <span className="font-medium">{r.name ?? "—"}</span>,
    },
    {
      key: "parent",
      header: "Đơn vị cha",
      width: "220px",
      render: (r) => <span className="text-sm">{r.parentName ?? "—"}</span>,
    },
    {
      key: "level",
      header: "Cấp",
      width: "60px",
      align: "center",
      render: (r) => <span className="tabular-nums">{r.level ?? "—"}</span>,
    },
    {
      key: "state",
      header: "Trạng thái",
      width: "110px",
      render: (r) =>
        r.isActive ? (
          <StatusBadge variant="success">Hoạt động</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">Ngưng</StatusBadge>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={TITLES.orgs.title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} đơn vị`}
        breadcrumb={TITLES.orgs.breadcrumb}
      />
      <SimpleSearchBar
        q={q}
        setQ={setQ}
        placeholder="Tìm theo mã, tên, viết tắt…"
        onSubmit={() => {
          setSubmittedQ(q);
          setPage(1);
        }}
      />
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.orgId}
        isLoading={isLoading}
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}

/* --- /admin/groups --- */
type GroupRow = {
  groupId: number;
  name: string | null;
  creatorName: string | null;
  creatorLogin: string | null;
  createdDate: string | null;
  groupType: number | null;
  scopeType: number | null;
  memberCount: number;
};
/** Ba ô lọc của lưới "Danh sách nhóm cá nhân" bên hệ cũ. */
type GroupFilters = { name: string; scopeType: string; groupType: string };
const EMPTY_GROUP_FILTERS: GroupFilters = { name: "", scopeType: "", groupType: "" };

function GroupsPanel() {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<GroupFilters>(EMPTY_GROUP_FILTERS);
  const [applied, setApplied] = useState<GroupFilters>(EMPTY_GROUP_FILTERS);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-groups", page, pageSize, applied],
    queryFn: async () => {
      const { data } = await api.get("/staff-group/list", {
        params: {
          page,
          pageSize,
          name: applied.name || undefined,
          scopeType: applied.scopeType || undefined,
          groupType: applied.groupType || undefined,
        },
      });
      return data.data ?? data;
    },
  });

  const columns: Column<GroupRow>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "action",
      header: "Thao tác",
      width: "90px",
      align: "center",
      render: (r) => (
        <RowActions
          actions={[
            {
              label: "Xem thành viên",
              icon: Eye,
              onClick: () => nav(`/admin/groups/${r.groupId}`),
            },
          ]}
        />
      ),
    },
    {
      key: "name",
      header: "Tên nhóm",
      render: (r) => <span className="font-medium">{r.name ?? "—"}</span>,
    },
    {
      key: "type",
      header: "Kiểu",
      width: "120px",
      render: (r) =>
        r.groupType === 2 ? (
          <StatusBadge variant="info">Theo đơn vị</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">Theo người</StatusBadge>
        ),
    },
    {
      key: "scope",
      header: "Phạm vi",
      width: "120px",
      render: (r) =>
        r.scopeType === 2 ? (
          <StatusBadge variant="primary">Chia sẻ</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">Riêng</StatusBadge>
        ),
    },
    {
      key: "creator",
      header: "Người tạo",
      width: "200px",
      render: (r) => <span className="text-sm">{r.creatorName ?? r.creatorLogin ?? "—"}</span>,
    },
    {
      key: "created",
      header: "Ngày tạo",
      width: "140px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.createdDate?.slice(0, 10) ?? "—"}</span>
      ),
    },
    {
      key: "members",
      header: "Thành viên",
      width: "100px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.memberCount}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title={TITLES.groups.title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} nhóm`}
        breadcrumb={TITLES.groups.breadcrumb}
      />
      <SearchPanel
        fields={[
          {
            label: "Tên nhóm",
            control: (
              <input
                className={filterInputClass}
                value={filters.name}
                onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
              />
            ),
          },
          {
            label: "Phạm vi sử dụng",
            control: (
              <select
                className={filterInputClass}
                value={filters.scopeType}
                onChange={(e) => setFilters((f) => ({ ...f, scopeType: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                <option value="1">Cá nhân</option>
                <option value="2">Đơn vị</option>
              </select>
            ),
          },
          {
            label: "Loại nhóm",
            control: (
              <select
                className={filterInputClass}
                value={filters.groupType}
                onChange={(e) => setFilters((f) => ({ ...f, groupType: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                <option value="1">Danh sách người dùng</option>
                <option value="2">Danh sách đơn vị</option>
              </select>
            ),
          },
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
      />
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.groupId}
        isLoading={isLoading}
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}

/* --- /admin/sign-flows --- */
type SignFlowRow = {
  id: number;
  name: string | null;
  createdBy: number | null;
  creatorName: string | null;
  creatorLogin: string | null;
  createdDate: string | null;
  permission: number | null;
  signerCount: number;
};
/** Hai ô lọc của lưới "Danh sách Luồng ký mẫu" bên hệ cũ. */
type FlowFilters = { name: string; permission: string };
const EMPTY_FLOW_FILTERS: FlowFilters = { name: "", permission: "" };

function SignFlowsPanel() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<FlowFilters>(EMPTY_FLOW_FILTERS);
  const [applied, setApplied] = useState<FlowFilters>(EMPTY_FLOW_FILTERS);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-sign-flows", page, pageSize, applied],
    queryFn: async () => {
      const { data } = await api.get("/text-sign/templates-admin", {
        params: {
          page,
          pageSize,
          name: applied.name || undefined,
          permission: applied.permission || undefined,
        },
      });
      return data.data ?? data;
    },
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => api.post(`/text-sign/templates/${id}/delete`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-sign-flows"] }),
  });

  const columns: Column<SignFlowRow>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "action",
      header: "Thao tác",
      width: "110px",
      align: "center",
      render: (r) => (
        <RowActions
          actions={[
            { label: "Sửa", icon: Pencil, onClick: () => nav(`/admin/sign-flows/${r.id}`) },
            {
              label: "Xóa",
              icon: Trash2,
              tone: "danger",
              disabled: removeMut.isPending,
              onClick: () => {
                if (window.confirm(`Xoá luồng ký mẫu "${r.name ?? r.id}"?`)) removeMut.mutate(r.id);
              },
            },
          ]}
        />
      ),
    },
    {
      key: "name",
      header: "Tên biểu mẫu",
      render: (r) => <span className="font-medium">{r.name ?? "—"}</span>,
    },
    {
      key: "creator",
      header: "Người tạo",
      width: "220px",
      render: (r) => <span className="text-sm">{r.creatorName ?? r.creatorLogin ?? "—"}</span>,
    },
    {
      key: "created",
      header: "Ngày tạo",
      width: "140px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.createdDate?.slice(0, 10) ?? "—"}</span>
      ),
    },
    {
      key: "signers",
      header: "Số người ký",
      width: "110px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.signerCount}</span>,
    },
    {
      key: "permission",
      header: "Phạm vi",
      width: "120px",
      render: (r) =>
        r.permission === 1 ? (
          <StatusBadge variant="primary">Chia sẻ</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">Riêng</StatusBadge>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={TITLES["sign-flows"].title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} mẫu luồng ký`}
        breadcrumb={TITLES["sign-flows"].breadcrumb}
      />
      <SearchPanel
        fields={[
          {
            label: "Tên biểu mẫu",
            control: (
              <input
                className={filterInputClass}
                value={filters.name}
                onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
              />
            ),
          },
          {
            label: "Phạm vi sử dụng",
            control: (
              <select
                className={filterInputClass}
                value={filters.permission}
                onChange={(e) => setFilters((f) => ({ ...f, permission: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                <option value="0">Cá nhân</option>
                <option value="1">Dùng chung đơn vị</option>
              </select>
            ),
          },
        ]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
      />
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.id}
        isLoading={isLoading}
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}

/* --- /admin/activity-log --- */
type ActivityRow = {
  id: number;
  userId: number | null;
  userName: string | null;
  loginName: string | null;
  actionTime: string | null;
  actionType: string | null;
  clientIp: string | null;
  status: string | null;
  description: string | null;
  roleName: string | null;
};
function ActivityLogPanel() {
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-activity-log", page, pageSize, submittedQ, days],
    queryFn: async () => {
      const { data } = await api.get("/admin/activity-log", {
        params: { page, pageSize: pageSize, userQ: submittedQ || undefined, days },
      });
      return data.data ?? data;
    },
  });

  const columns: Column<ActivityRow>[] = [
    {
      key: "time",
      header: "Thời gian",
      width: "150px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.actionTime?.replace("T", " ") ?? "—"}</span>
      ),
    },
    {
      key: "user",
      header: "Người dùng",
      width: "180px",
      render: (r) => <span className="font-medium">{r.userName ?? "—"}</span>,
    },
    {
      key: "login",
      header: "Login",
      width: "120px",
      render: (r) => <span className="font-mono text-xs">{r.loginName ?? "—"}</span>,
    },
    {
      key: "action",
      header: "Hành động",
      width: "160px",
      render: (r) => <span className="text-sm">{r.actionType ?? "—"}</span>,
    },
    {
      key: "ip",
      header: "IP",
      width: "130px",
      render: (r) => <span className="font-mono text-xs">{r.clientIp ?? "—"}</span>,
    },
    {
      key: "desc",
      header: "Mô tả",
      render: (r) => <span className="text-sm text-muted-foreground">{r.description ?? "—"}</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "100px",
      render: (r) =>
        r.status === "1" || r.status === "OK" ? (
          <StatusBadge variant="success">{r.status}</StatusBadge>
        ) : r.status ? (
          <StatusBadge variant="neutral">{r.status}</StatusBadge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={TITLES["activity-log"].title}
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} hoạt động · ${days} ngày gần nhất`}
        breadcrumb={TITLES["activity-log"].breadcrumb}
      />
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 max-w-md relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            className={inputClass}
            placeholder="Tìm theo người dùng, login, mô tả…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSubmittedQ(q);
                setPage(1);
              }
            }}
          />
        </div>
        <select
          className="h-9 px-3 rounded-md border border-input bg-background text-sm"
          value={days}
          onChange={(e) => {
            setDays(Number(e.target.value));
            setPage(1);
          }}
        >
          <option value={7}>7 ngày</option>
          <option value={30}>30 ngày</option>
          <option value={90}>90 ngày</option>
          <option value={180}>180 ngày</option>
          <option value={365}>1 năm</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSubmittedQ(q);
            setPage(1);
          }}
        >
          Tìm kiếm
        </Button>
      </div>
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.id}
        isLoading={isLoading}
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}

export function AdminPage({ kind }: { kind: Kind }) {
  if (kind === "users") return <UsersPanel />;
  if (kind === "roles") return <RolesPanel />;
  if (kind === "orgs") return <OrgsPanel />;
  if (kind === "groups") return <GroupsPanel />;
  if (kind === "sign-flows") return <SignFlowsPanel />;
  if (kind === "activity-log") return <ActivityLogPanel />;
  return null;
}
